/// @author Cyrill Tekord

import * as _ from 'lodash';
import {Field, ValueRetrieverCallback} from "./field";

interface FillValueOptions {
    valueSetCallback?: ((field: Field) => void);
    finishedCallback?: ((fields: Field[]) => void);
}

export class Form {
    static map<FieldType, ValueRetrieverResultType>(
        fields: Field<FieldType>[],
        callback: ValueRetrieverCallback<FieldType, ValueRetrieverResultType>,
    ) {
        let result: { [key: string]: any } = {};

        for (let field of fields) {
            if (field.name === null) {
                console.warn('Unable to map value for a field without name', field);

                continue;
            }

            result[field.name] = callback(field);
        }

        return result;
    }

    static transform<FieldType>(
        fields: Field<FieldType>[],
        callback: (field: Field<FieldType>) => void,
    ) {
        fields.forEach(callback);
    }

    static extractErrorList(
        fields: Field[],
    ) {
        let result: string[] = [];

        for (let field of fields) {
            result.push(...field.errors);
        }

        return result;
    }

    static setErrors(
        fields: Field[],
        errorBag: { [key: string]: any },
    ) {
        Form.transform(fields, (field) => {
            field.errors.clear();

            if (field.name === null)
                return;

            let errors = _.get(errorBag, field.name, undefined);

            if (errors !== undefined) {
                field.errors.push(...errors);
            }
        });
    }

    static dumpValues<FieldType, ValueRetrieverResultType>(
        fields: Field<FieldType>[],
        overriddenValueDumperCallback: ValueRetrieverCallback<FieldType, ValueRetrieverResultType> | null = null,
    ) {
        return Form.map(fields, (field) => {
            if (overriddenValueDumperCallback)
                return overriddenValueDumperCallback(field);
            else
                return field.dumpValue();
        });
    }

    static fillValues(
        fields: Field[],
        valueBag: any,
        options?: FillValueOptions,
    ) {
        for (let field of fields) {
            if (!field.name)
                continue;

            if (valueBag.hasOwnProperty(field.name)) {
                let valueToSet = valueBag[field.name];

                field.setValue(valueToSet);

                if (options?.valueSetCallback)
                    options?.valueSetCallback(field);
            }
        }

        if (options?.finishedCallback)
            options?.finishedCallback(fields);
    }

    static fillFormData(
        dumpedValues: { [key: string]: any },
        formData: FormData,
        nameTransformer: ((name: string) => string) | null = null,
    ) {
        for (const [rawName, value] of Object.entries(dumpedValues)) {
            let name = rawName;

            if (nameTransformer)
                name = nameTransformer(name);

            if (value instanceof Array)
                value.forEach((i) => formData.append(name + "[]", i));
            else
                formData.append(name, value);
        }

        return formData;
    }
}
